import { useMemo } from "react";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { FleetManagementRow } from "../../FleetManagement/types/FleetManagement-row.types";
import { FleetFuelType } from "../../FleetManagement/types/FleetManagement.types";
import * as _ from "lodash";

export type IdName = { id: string; name: string };

export function useDepartmentOptions(): IdName[] {
  const { data: remoteUsers } = useRemoteFetchUserInfoForAllUsers();
  return useMemo(() => {
    if (!remoteUsers?.length) return [];
    const departments = remoteUsers
      .filter(u => u.departmentId != null && u.departmentName)
      .map(u => ({ id: u.departmentId as string, name: u.departmentName }));
    return _.uniqBy(departments, d => d.id);
  }, [remoteUsers]);
}

/** Optional hook for deriving vehicle type / fuel / property options from vehicles (e.g. for tests or future filter tabs). Not used in this slideout (weekday + departments only). */
export function useFleetOptionsFromVehicles(
  vehicles: FleetManagementRow[] | undefined,
  t: (key: string) => string
): {
  vehicleTypeOpts: IdName[];
  fuelOpts: IdName[];
  propertyOpts: IdName[];
} {
  return useMemo(() => {
    const list = vehicles ?? [];
    const vehicleTypeOpts: IdName[] =
      list.length > 0
        ? _.uniqBy(
            list
              .filter(v => v.vehicleType != null && v.vehicleType !== "")
              .map(v => ({ id: v.vehicleType as string, name: v.vehicleType as string })),
            d => d.id
          )
        : [];

    const vehicleFuelIds = new Set(list.map(v => v.fuel));
    const fuelOpts: IdName[] =
      list.length > 0
        ? Object.values(FleetFuelType)
            .filter(f => vehicleFuelIds.has(f))
            .map(f => ({ id: f, name: t(f) }))
        : [];

    const propertyOpts: IdName[] =
      list.length > 0
        ? _.uniqBy(
            list.flatMap(v => (v.properties ?? []).map(p => ({ id: String(p.id), name: p.name }))),
            d => d.id
          )
        : [];

    return { vehicleTypeOpts, fuelOpts, propertyOpts };
  }, [vehicles, t]);
}
